import { ContestTaskType } from '../contest/ContestTask';
import { SchoolGrade } from '../location/School';
import { ContestEvent, ContestEventDto } from '../contest/ContestEvent';

export abstract class Stats {
  public type?: StatsType;
  public grouping?: StatsGroupingType;

  public regionId?: string;
  public schoolId?: string;
  public schoolName?: string;

  public static parse(data: StatsDto, stats: Stats) {
    stats.type = data.type;
    stats.grouping = data.grouping;

    stats.regionId = data.region_id;
    stats.schoolId = data.school_id;
    stats.schoolName = data.school_name;
  }
}

export class EnrolStats extends Stats {
  public status_scheduled?: number;
  public status_pending?: number;
  public status_successful?: number;
  public status_failed?: number;

  public grade_2?: number;
  public grade_3?: number;
  public grade_4?: number;
  public grade_5?: number;
  public grade_6?: number;
  public grade_7?: number;
  public grade_8?: number;
  public grade_9?: number;
  public grade_10?: number;
  public grade_11?: number;

  public user_status_confirmed?: number;
  public user_status_unconfirmed?: number;
  public user_status_other?: number;

  public static parse(data: EnrolStatsDto): EnrolStats {
    const stats = new EnrolStats();

    super.parse(data, stats);

    stats.status_scheduled = data.status_scheduled || 0;
    stats.status_pending = data.status_pending || 0;
    stats.status_successful = data.status_successful || 0;
    stats.status_failed = data.status_failed || 0;

    stats.grade_2 = data.grade_2 || 0;
    stats.grade_3 = data.grade_3 || 0;
    stats.grade_4 = data.grade_4 || 0;
    stats.grade_5 = data.grade_5 || 0;
    stats.grade_6 = data.grade_6 || 0;
    stats.grade_7 = data.grade_7 || 0;
    stats.grade_8 = data.grade_8 || 0;
    stats.grade_9 = data.grade_9 || 0;
    stats.grade_10 = data.grade_10 || 0;
    stats.grade_11 = data.grade_11 || 0;

    stats.user_status_confirmed = 0;
    stats.user_status_unconfirmed = 0;
    stats.user_status_other = 0;

    Object.keys(data)
      .filter(key => key.startsWith('s_'))
      .forEach(key => {
        const s = (data as unknown) as { [key: string]: string | number | unknown };
        if (key == 's_confirmed' && stats.user_status_confirmed != undefined) {
          stats.user_status_confirmed += s[key] ? s[key] as number : 0;
        } else if (key == 's_force_change_password' && stats.user_status_unconfirmed != undefined) {
          stats.user_status_unconfirmed += s[key] ? s[key] as number : 0;
        } else if (stats.user_status_other != undefined) {
          stats.user_status_other += s[key] ? s[key] as number : 0;
        }
      });

    return stats;
  }
}

export class ContestPartStats extends EnrolStats {
  public status_canceled?: number;

  public status_teacher_successful?: number;
  public status_teacher_review?: number;
  public status_teacher_rejected?: number;

  public status_coordinator_successful?: number;

  public exam_participants?: number;

  public exam_school_grade_2?: number;
  public exam_school_grade_3?: number;
  public exam_school_grade_4?: number;
  public exam_school_grade_5?: number;
  public exam_school_grade_6?: number;
  public exam_school_grade_7?: number;
  public exam_school_grade_8?: number;
  public exam_school_grade_9?: number;
  public exam_school_grade_10?: number;
  public exam_school_grade_11?: number;

  public exam_points_grade_excellent?: number;
  public exam_points_grade_good?: number;
  public exam_points_grade_average?: number;

  public static parse(data: ContestPartStatsDto): ContestPartStats {
    const enrolStats = super.parse(data);

    const stats = new ContestPartStats();

    const enrolStatsElem = enrolStats as { [key: string]: string | number };
    const statsElem = stats as { [key: string]: string | number };
    for (const key of Object.keys(enrolStatsElem)) {
      statsElem[key] = enrolStatsElem[key];
    }

    stats.status_canceled = data.status_canceled || 0;

    stats.status_teacher_successful = data.status_Teacher_successful || 0;
    stats.status_teacher_review = data.status_Teacher_review || 0;
    stats.status_teacher_rejected = data.status_Teacher_rejected || 0;

    stats.status_coordinator_successful = data.status_Coordinator_successful || 0;

    stats.exam_participants = data.exam_participants || 0;

    stats.exam_school_grade_2 = data.exam_school_grade_2 || 0;
    stats.exam_school_grade_3 = data.exam_school_grade_3 || 0;
    stats.exam_school_grade_4 = data.exam_school_grade_4 || 0;
    stats.exam_school_grade_5 = data.exam_school_grade_5 || 0;
    stats.exam_school_grade_6 = data.exam_school_grade_6 || 0;
    stats.exam_school_grade_7 = data.exam_school_grade_7 || 0;
    stats.exam_school_grade_8 = data.exam_school_grade_8 || 0;
    stats.exam_school_grade_9 = data.exam_school_grade_9 || 0;
    stats.exam_school_grade_10 = data.exam_school_grade_10 || 0;
    stats.exam_school_grade_11 = data.exam_school_grade_11 || 0;

    stats.exam_points_grade_excellent = data.exam_points_grade_excellent || 0;
    stats.exam_points_grade_good = data.exam_points_grade_good || 0;
    stats.exam_points_grade_average = data.exam_points_grade_average || 0;

    return stats;
  }
}

export class UserExamStats extends Stats {

  public userId?: string;

  public taskId?: string;
  public taskType?: ContestTaskType;

  public email?: string;
  public firstName?: string;
  public lastName?: string;

  public schoolGrade?: SchoolGrade;

  public initPoints?: number;
  public points?: number;

  public timeSpent?: number;

  public bonusPoints?: number;
  public bonusDescription?: string;

  public grade?: UserExamGrade;

  public static parse(data: UserExamStatsDto): UserExamStats {
    const stats = new UserExamStats();

    super.parse(data, stats);

    stats.userId = data.user_id;

    stats.taskId = data.task_id;
    stats.taskType = data.task_type;

    stats.email = data.email;
    stats.firstName = data.first_name;
    stats.lastName = data.last_name;

    stats.schoolGrade = data.school_grade;

    stats.initPoints = data.init_points || 0;
    stats.points = data.points || 0;

    if (data.time_spent) stats.timeSpent = data.time_spent;

    if (data.bonus_points) stats.bonusPoints = data.bonus_points;
    if (data.bonus_points) stats.bonusDescription = data.bonus_description;

    stats.grade = data.grade;

    return stats;
  }
}

export class ExamCardStats {
  public exam?: UserExamStats;
  public events: ContestEvent[] = [];
  public participated = false;

  public static parse(data: ExamCardStatsDto): ExamCardStats {
    const stats = new ExamCardStats();

    if (data.exam) stats.exam = UserExamStats.parse(data.exam);
    if (data.events) stats.events = data.events.map(event => ContestEvent.parse(event));
    stats.participated = data.participated || false;

    return stats;
  }
}

export class TeacherExamStats extends Stats {
  public userId?: string;

  public email?: string;
  public phone?: string;
  public firstName?: string;
  public lastName?: string;

  public studentsCount?: number;
  public enrolledStudentsCount?: number;

  public enrolledAt?: Date;

  public points?: number;

  public static parse(data: TeacherExamStatsDto): TeacherExamStats {
    const stats = new TeacherExamStats();

    super.parse(data, stats);

    stats.userId = data.user_id;

    stats.email = data.email;
    stats.phone = data.phone;
    stats.firstName = data.first_name;
    stats.lastName = data.last_name;

    stats.studentsCount = data.students_count || 0;
    stats.enrolledStudentsCount = data.enrolled_students_count || 0;

    stats.points = data.points || 0;

    return stats;
  }
}

export class LocationExamStats extends Stats {
  public schoolAddress?: string;
  public schoolRegionName?: string;

  public skipped?: number;
  public teachers?: number;

  public excellentResults?: number;
  public goodResults?: number;
  public averageResults?: number;

  public static parse(data: LocationExamStatsDto): LocationExamStats {
    const stats = new LocationExamStats();

    super.parse(data, stats);

    stats.schoolAddress = data.school_address;
    stats.schoolRegionName = data.school_region_name;

    stats.excellentResults = data['exam_grade|excellent'] || 0;
    stats.goodResults = data['exam_grade|good'] || 0;
    stats.averageResults = data['exam_grade|average'] || 0;

    stats.skipped = data.skipped || 0;
    stats.teachers = data.teachers || 0;

    return stats;
  }
}

export interface ExamCardStatsDto {
  exam?: UserExamStatsDto;
  events?: ContestEventDto[];
  participated: boolean;
}

export interface StatsDto {
  type?: StatsType;
  grouping?: StatsGroupingType;

  region_id?: string;
  school_id?: string;
  school_name?: string;
}

export interface EnrolStatsDto extends StatsDto {
  status_scheduled?: number;
  status_pending?: number;
  status_successful?: number;
  status_failed?: number;

  grade_2?: number;
  grade_3?: number;
  grade_4?: number;
  grade_5?: number;
  grade_6?: number;
  grade_7?: number;
  grade_8?: number;
  grade_9?: number;
  grade_10?: number;
  grade_11?: number;
}

export interface ContestPartStatsDto extends EnrolStatsDto {
  status_canceled?: number;

  status_Teacher_successful?: number;
  status_Teacher_review?: number;
  status_Teacher_rejected?: number;

  status_Coordinator_successful?: number;

  exam_participants?: number;

  exam_school_grade_2?: number;
  exam_school_grade_3?: number;
  exam_school_grade_4?: number;
  exam_school_grade_5?: number;
  exam_school_grade_6?: number;
  exam_school_grade_7?: number;
  exam_school_grade_8?: number;
  exam_school_grade_9?: number;
  exam_school_grade_10?: number;
  exam_school_grade_11?: number;

  exam_points_grade_excellent?: number;
  exam_points_grade_good?: number;
  exam_points_grade_average?: number;
}

export interface LocationExamStatsDto extends StatsDto {
  school_address?: string;
  school_region_name?: string;

  skipped?: number;
  teachers?: number;

  'exam_grade|excellent'?: number;
  'exam_grade|good'?: number;
  'exam_grade|average'?: number;
}

export interface UserExamStatsDto extends StatsDto {
  user_id?: string;

  task_id?: string;
  task_type?: ContestTaskType;

  email?: string;
  first_name?: string;
  last_name?: string;

  school_grade?: SchoolGrade;

  init_points?: number;
  points?: number;
  time_spent?: number;

  bonus_points?: number;
  bonus_description?: string;

  grade?: UserExamGrade;
}

export interface TeacherExamStatsDto extends StatsDto {
  user_id?: string;

  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;

  students_count: number;
  enrolled_students_count: number;

  points?: number;
}

export enum StatsType {
  enrol = 'enrol',
  exam = 'exam',
  user_exam = 'user-exam',
  user_exam_card = 'user-exam-card',
  user_part = 'user-part',
  teacher_exam = 'teacher-exam',
  contest_part = 'contest-part',
}

export enum StatsGroupingType {
  region = 'region',
  school = 'school',
  user = 'user',
  contest = 'contest',
}

export enum UserExamGrade {
  excellent = 'excellent',
  good = 'good',
  average = 'average',
}

export interface UserExamGradeStats {
  excellent_count: number;
  good_count: number;
  average_count: number;
}


export function localizeUserExamGrade(grade: UserExamGrade): string {
  let value: string = '';

  switch (grade) {
    case UserExamGrade.excellent:
      value = $localize`:General|User exam grade excellent title@@general-user-exam-grade-excellent-title:Excellent`;
      break;
    case UserExamGrade.good:
      value = $localize`:General|User exam grade good title@@general-user-exam-grade-good-title:Good`;
      break;
    case UserExamGrade.average:
      value = $localize`:General|User exam grade average title@@general-user-exam-grade-average-title:Average`;
      break;

  }

  return value;
}

export enum StatsExportType {
  csv = 'csv'
}
